import type { Column } from "@shared/types/data-table"
import type { Customer } from "@shared/types/customers"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import {
  createTextColumn,
  createStatusColumn,
  createLocationColumn,
  createCurrencyColumn,
  createDateColumn,
  createTruncatedColumn,
  createCustomColumn,
  createIconTextColumn
} from "./column-factories"
import { AppStatusBadge } from "@shared/components/ui/status-badge/AppStatusBadge"
import { CurrencyCell, TextCell } from "../cells/common-cells"
import { CreditCard } from "lucide-react"
import { CustomerTypeBadge } from "@features/customers/components/CustomerTypeBadge"

export const CUSTOMER_COLUMNS: Record<string, Column<Customer>> = {
  name: createTextColumn<Customer>("name", "Name", 200, "font-bold"),
  status: { ...createStatusColumn<Customer>("customer"), header: "" },
  description: createTruncatedColumn<Customer>(
    "description",
    "Description",
    200,
    (customer) => customer.description || "—",
    "text-sm text-muted-foreground"
  ),
  risk: createCustomColumn<Customer>(
    "risk",
    "Risk",
    100,
    (customer: Customer) => {
      const riskScore = customer.riskScore ?? 0
      const riskColor = !customer.riskLevel
        ? 'neutral'
        : riskScore >= 40 ? 'error' : riskScore >= 30 ? 'warning' : 'success'
      
      return (
        <div className="flex items-center gap-2">
          <AppStatusBadge 
            variant="generic" 
            text={customer.riskLevel || 'N/A'}
            color={riskColor}
          />
          {customer.riskScore && (
            <span className="text-xs text-muted-foreground">{customer.riskScore}</span>
          )}
        </div>
      )
    }
  ),
  reason: createTruncatedColumn<Customer>(
    "reason",
    "Reason",
    150,
    (customer) => customer.reason || "N/A",
    "text-sm text-muted-foreground"
  ),
  defaultPaymentMethod: createIconTextColumn<Customer>(
    "defaultPaymentMethod",
    "Default payment method",
    180,
    <CreditCard className="h-3 w-3 text-muted-foreground" />,
    (customer) => customer.defaultPaymentMethod || "None"
  ),
  cardholderName: createTruncatedColumn<Customer>(
    "cardholderName",
    "Cardholder name",
    150,
    (customer) => customer.cardholderName || "N/A",
    "text-sm text-muted-foreground"
  ),
  location: createLocationColumn<Customer>(150),
  totalSpent: createCurrencyColumn<Customer>("totalSpent", "LTV ($)"),
  refunds: createCustomColumn<Customer>(
    "refunds",
    "Refunds ($)",
    150,
    (customer: Customer) => (
      <div className="text-right">
        <CurrencyCell amount={customer.refunds ?? 0} />
        <div className="text-xs text-muted-foreground">
          #{customer.refundCount ?? 0}
        </div>
      </div>
    )
  ),
  disputeLosses: createCustomColumn<Customer>(
    "disputeLosses",
    "Dispute losses ($)",
    150,
    (customer: Customer) => (
      <div className="text-right">
        <CurrencyCell amount={customer.disputeLosses ?? 0} />
        <div className="text-xs text-muted-foreground">
          #{customer.disputeCount ?? 0}
        </div>
      </div>
    )
  ),
  created: createDateColumn<Customer>("created", "Created"),
  restricted: createDateColumn<Customer>("restricted", "Restricted"),
  terminated: createDateColumn<Customer>("terminated", "Terminated"),
  lastActivity: createDateColumn<Customer>("lastActivity", "Last activity"),
  delinquent: createCustomColumn<Customer>(
    "delinquent",
    "Delinquent",
    100,
    (customer: Customer) => {
      const value = customer.delinquent
      if (value === null || value === undefined) return <TextCell value="—" />

      return (
        <AppStatusBadge
          variant="generic"
          text={value ? "Yes" : "No"}
          color={value ? "error" : "success"}
        />
      )
    }
  ),
  type: createCustomColumn<Customer>(
    "type",
    "Type",
    140,
    (customer: Customer) => {
      if (!customer.type) return <TextCell value="—" />
      return <CustomerTypeBadge type={customer.type} />
    }
  ),
  businessVatId: createTextColumn<Customer>("businessVatId", "Business VAT ID", 140),
  taxLocationRecognized: createCustomColumn<Customer>(
    "taxLocationRecognized",
    "Tax location recognized",
    160,
    (customer: Customer) => {
      const value = customer.taxLocationRecognized
      if (value === null || value === undefined) return <TextCell value="—" />
      
      return (
        <AppStatusBadge 
          variant="generic" 
          text={value ? "Yes" : "No"}
          color={value ? "success" : "neutral"}
        />
      )
    }
  ),
  accountBalance: createCurrencyColumn<Customer>("accountBalance", "Account balance"),
  averageOrder: createCurrencyColumn<Customer>("averageOrder", "Average order"),
  spend: createCurrencyColumn<Customer>("spend", "Spend ($)"),
  store: createTextColumn<Customer>("store", "Store", 120),
  merchant: createTextColumn<Customer>("merchant", "Merchant", 120),
  program: createTextColumn<Customer>("program", "Program", 120),
  organization: createTextColumn<Customer>("organization", "Organization", 140),
  bank: createTextColumn<Customer>("bank", "Bank", 120),
  productPlatform: createTextColumn<Customer>("productPlatform", "Product platform", 140),
  processor: createTextColumn<Customer>("processor", "Processor", 120),
}

export const CUSTOMER_STATUS_CONFIG = {
  all:          ['name', 'status', 'description', 'defaultPaymentMethod', 'location', 'created', 'lastActivity'],
  active:       ['name', 'status', 'description', 'defaultPaymentMethod', 'location', 'created', 'lastActivity'],
  under_review: ['name', 'status', 'reason', 'defaultPaymentMethod', 'location', 'created', 'lastActivity'],
  restricted:   ['name', 'status', 'reason', 'defaultPaymentMethod', 'location', 'created', 'restricted', 'lastActivity'],
  terminated:   ['name', 'status', 'reason', 'defaultPaymentMethod', 'location', 'created', 'terminated', 'lastActivity'],
} as const

export const getCustomerColumns = (status: string = 'all'): Column<Customer>[] => {
  const keys = CUSTOMER_STATUS_CONFIG[status as keyof typeof CUSTOMER_STATUS_CONFIG] || CUSTOMER_STATUS_CONFIG.all
  return keys.map(key => CUSTOMER_COLUMNS[key]).filter(Boolean)
}

export const getCustomerColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(CUSTOMER_STATUS_CONFIG[status as keyof typeof CUSTOMER_STATUS_CONFIG] || CUSTOMER_STATUS_CONFIG.all)
  return Object.entries(CUSTOMER_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ['name', 'status'].includes(key),
    order: index
  }))
}